import {getHrefForLinkType, getRelAttributes, getTargetAttribute} from 'Application/utils/linkFieldUtils';
import {isDefined} from 'Application/utils/typeGuards';

import type {FooterColumn} from 'types/payload-types';

/**
 * The `useContactColumn` function processes a FooterColumn to extract and format contact information for phone and email links.
 * This hook transforms the raw column data into structured objects containing all necessary link attributes including href, label, rel, and target values.
 * It handles both phone and email links safely by checking if they are defined before processing them through the link utility functions.
 *
 * @param column The FooterColumn object containing potential phone and email link data.
 * @returns An object containing formatted phone and email link properties with labels, URLs, rel attributes, and target attributes.
 *
 * @example
 * ```tsx
 * const column = {
 *   phoneLink: { label: 'Call us', url: 'tel:+1234567890', type: 'custom' },
 *   mailToLink: { label: 'Email us', url: 'mailto:info@example.com', type: 'custom' }
 * };
 *
 * const {
 *   phoneLabel,
 *   phoneLink,
 *   mailLabel,
 *   mailToLink
 * } = useContactColumn(column);
 * ```
 */
export const useContactColumn = (column: FooterColumn) => {
    let phoneLink = '';
    let phoneLabel = '';
    let phoneRel = '';
    let phoneTarget = '';
    let mailToLink = '';
    let mailLabel = '';
    let mailRel = '';
    let mailTarget = '';

    if (isDefined(column.phoneLink)) {
        phoneLink = getHrefForLinkType(column.phoneLink);
        phoneLabel = column.phoneLink.label;
        phoneRel = getRelAttributes(column.phoneLink) ?? '';
        phoneTarget = getTargetAttribute(column.phoneLink) ?? '';
    }
    if (isDefined(column.mailToLink)) {
        mailToLink = getHrefForLinkType(column.mailToLink);
        mailLabel = column.mailToLink.label;
        mailRel = getRelAttributes(column.mailToLink) ?? '';
        mailTarget = getTargetAttribute(column.mailToLink) ?? '';
    }

    return {
        mailLabel,
        mailRel,
        mailTarget,
        mailToLink,
        phoneLabel,
        phoneLink,
        phoneRel,
        phoneTarget
    };
};